import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import CheckoutModal from './components/CheckoutModal';

const LandingPage = () => {
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const handleStartFreeTrial = (planType = 'growth') => {
    setSelectedPlan(planType);
    setIsCheckoutOpen(true);
  };

  // Mouse tracking for interactive elements
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e?.clientX, y: e?.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const hrCapabilities = [
    {
      icon: 'Users',
      title: 'Employee Directory & Profiles',
      description: 'Comprehensive employee management with detailed profiles, organizational tracking, and advanced filtering capabilities.',
      gradient: 'from-blue-500 via-indigo-500 to-blue-600',
      color: 'blue'
    },
    {
      icon: 'UserPlus',
      title: 'Onboarding & Offboarding',
      description: 'Streamlined employee lifecycle management with automated workflows, task assignment, and completion tracking.',
      gradient: 'from-emerald-500 via-teal-500 to-emerald-600',
      color: 'emerald'
    },
    {
      icon: 'Calendar',
      title: 'Time Off Management',
      description: 'Intelligent PTO policies, request workflows, team calendar integration, and automated accrual tracking.',
      gradient: 'from-purple-500 via-pink-500 to-purple-600',
      color: 'purple'
    },
    {
      icon: 'TrendingUp',
      title: 'Performance & 1:1s',
      description: 'Goal management, review cycles, continuous feedback, and one-on-one meeting facilitation.',
      gradient: 'from-amber-500 via-orange-500 to-amber-600',
      color: 'amber'
    },
    {
      icon: 'FileText',
      title: 'Documents & Compliance',
      description: 'Policy management, digital acknowledgments, audit trails, and compliance tracking for regulatory requirements.',
      gradient: 'from-slate-500 via-gray-500 to-slate-600',
      color: 'slate'
    },
    {
      icon: 'Brain',
      title: 'AI-Powered Recruiting',
      description: 'Intelligent candidate screening, automated pipelines, interview scheduling, and data-driven hiring insights.',
      gradient: 'from-indigo-500 via-purple-500 to-indigo-600',
      color: 'indigo'
    },
    {
      icon: 'DollarSign',
      title: 'Compensation Management',
      description: 'Salary bands, promotion workflows, equity tracking, and compensation analysis tools.',
      gradient: 'from-green-500 via-emerald-500 to-green-600',
      color: 'green'
    },
    {
      icon: 'Zap',
      title: 'Automation Rules',
      description: 'Smart workflow automation for HR processes, notifications, and task management across all modules.',
      gradient: 'from-violet-500 via-indigo-500 to-violet-600',
      color: 'violet'
    }
  ];

  const testimonials = [];

  const companyLogos = [];

  // Auto-rotate testimonials (disabled when empty)
  useEffect(() => {
    if (testimonials?.length === 0) return;
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials?.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [testimonials?.length]);

  const stats = [
    { label: 'HR Processes', value: '8+', change: 'Modules', trend: 'up' },
    { label: 'Setup Time', value: '<5min', change: 'Quick Start', trend: 'down' },
    { label: 'Automation', value: '100%', change: 'Customizable', trend: 'up' },
    { label: 'Integration', value: '24/7', change: 'Support', trend: 'up' }
  ];

  return (
    <>
      <Helmet>
        <title>Complete HR Platform - Employee Management + AI Recruiting - Intime</title>
        <meta name="description" content="Intime is a comprehensive HR platform combining employee management, performance tracking, time-off management, and AI-powered recruiting in one unified system." />
        <meta name="keywords" content="HR platform, employee management, performance tracking, time off, recruiting, ATS, onboarding, compensation, automation" />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="Complete HR Platform - Employee Management + AI Recruiting - Intime" />
        <meta property="og:description" content="Comprehensive HR platform combining employee management, performance tracking, and AI-powered recruiting." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://intime.com" />
        
        {/* Additional SEO tags */}
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://intime.com" />
      </Helmet>
      <div className="min-h-screen bg-white font-['Inter',sans-serif] antialiased">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 border-b border-slate-200/60 bg-white/80 backdrop-blur-xl supports-[backdrop-filter]:bg-white/60">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              {/* Logo */}
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <div className="w-9 h-9 bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-700 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-500/25">
                    <Icon name="Zap" size={18} className="text-white" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-700 rounded-2xl blur opacity-30 animate-pulse"></div>
                </div>
                <span className="text-xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent tracking-tight">
                  Intime
                </span>
              </div>
              
              {/* Navigation */}
              <nav className="hidden md:flex items-center space-x-1">
                <Link 
                  to="/pricing" 
                  className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-50 rounded-lg transition-all duration-200"
                >
                  Pricing
                </Link>
                <div className="w-px h-4 bg-slate-300 mx-2"></div>
                <Link 
                  to="/login" 
                  className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-50 rounded-lg transition-all duration-200"
                >
                  Sign in
                </Link>
                <Button 
                  onClick={() => handleStartFreeTrial('growth')}
                  className="ml-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium px-4 py-2 text-sm rounded-lg shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40 transition-all duration-200 transform hover:scale-105"
                >
                  Start Free
                </Button>
              </nav>

              {/* Mobile menu button */}
              <div className="md:hidden">
                <Button variant="ghost" size="icon" className="hover:bg-slate-50 rounded-lg">
                  <Icon name="Menu" size={20} />
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-indigo-50/40 to-purple-50/30"></div>
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-gradient-to-br from-indigo-100/60 via-purple-100/40 to-pink-100/30 rounded-full blur-3xl opacity-70 animate-pulse"></div>
          
          {/* Interactive Grid */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#f1f5f9_1px,transparent_1px),linear-gradient(to_bottom,#f1f5f9_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_110%)] opacity-30"></div>
          
          <div className="max-w-7xl mx-auto relative">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Left Column - Content */}
              <div className="text-left space-y-8">
                <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200/50 text-indigo-700 rounded-full text-sm font-medium shadow-sm">
                  <div className="w-2 h-2 bg-indigo-500 rounded-full mr-2 animate-pulse"></div>
                  Complete HR Platform + AI Recruiting
                </div>
                
                <div className="space-y-6">
                  <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-slate-900 leading-[0.95] tracking-tight">
                    Complete HR platform for{' '}
                    <span className="relative">
                      <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 bg-clip-text text-transparent">
                        modern teams
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 bg-clip-text text-transparent blur-sm opacity-30"></div>
                    </span>
                  </h1>
                  
                  <p className="text-xl text-slate-600 leading-relaxed max-w-xl font-medium">
                    Employee management, performance tracking, time-off, recruiting, and automation. 
                    Everything HR teams need in one powerful, integrated platform.
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    onClick={() => handleStartFreeTrial('growth')}
                    size="lg" 
                    className="group relative bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-8 py-4 text-lg font-semibold rounded-xl shadow-xl shadow-indigo-500/25 hover:shadow-indigo-500/40 transition-all duration-300 transform hover:scale-105"
                  >
                    <span className="relative z-10 flex items-center">
                      Start Free Trial
                      <Icon name="ArrowRight" size={20} className="ml-2 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-indigo-700 to-purple-700 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </Button>
                  <Button 
                    variant="outline" 
                    size="lg"
                    className="border-2 border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 px-8 py-4 text-lg font-semibold rounded-xl transition-all duration-200 hover:shadow-lg"
                  >
                    Watch demo
                    <Icon name="Play" size={18} className="ml-2" />
                  </Button>
                </div>

                {/* Trust Indicators */}
                <div className="flex flex-wrap items-center gap-6 pt-4">
                  {['Free up to 10 employees', 'No credit card required', '5-minute setup']?.map((indicator, index) => (
                    <div key={index} className="flex items-center space-x-2 text-sm text-slate-600">
                      <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                      <span className="font-medium">{indicator}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Column - Interactive Dashboard */}
              <div className="relative lg:ml-8">
                {/* Main Dashboard Card */}
                <div className="relative bg-white/70 backdrop-blur-sm rounded-3xl shadow-2xl border border-slate-200/50 p-8 hover:shadow-3xl transition-all duration-500">
                  {/* Dashboard Header */}
                  <div className="flex items-center justify-between pb-6 border-b border-slate-100">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">HR Dashboard</h3>
                      <p className="text-sm text-slate-500">Organizational overview</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                      <span className="text-sm text-slate-600 font-medium">Live</span>
                    </div>
                  </div>
                  
                  {/* HR Module Cards */}
                  <div className="space-y-4 py-6">
                    {[
                      { name: 'Employee Directory', icon: 'Users', status: 'Ready to setup', color: 'blue' },
                      { name: 'Time Off Management', icon: 'Calendar', status: 'Configure policies', color: 'green' },
                      { name: 'Performance Reviews', icon: 'TrendingUp', status: 'Create first cycle', color: 'purple' },
                      { name: 'AI Recruiting', icon: 'Brain', status: 'Post first job', color: 'indigo' }
                    ]?.map((module, index) => (
                      <div key={index} className="group flex items-center justify-between p-4 bg-gradient-to-r from-slate-50/80 to-white rounded-xl border border-slate-100 hover:border-slate-200 hover:shadow-md transition-all duration-200">
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-10 bg-gradient-to-br from-${module?.color}-500 to-${module?.color}-600 rounded-full flex items-center justify-center shadow-lg shadow-${module?.color}-500/30`}>
                            <Icon name={module?.icon} size={16} className="text-white" />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">{module?.name}</p>
                            <p className="text-sm text-slate-600">{module?.status}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Icon name="ChevronRight" size={16} className="text-slate-400 group-hover:text-slate-600 transition-colors" />
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Setup Progress */}
                  <div className="pt-6 border-t border-slate-100">
                    <div className="flex items-center justify-between text-sm mb-3">
                      <span className="text-slate-600 font-medium">Setup Progress</span>
                      <div className="flex items-center space-x-1">
                        <Icon name="Zap" size={16} className="text-indigo-600" />
                        <span className="font-semibold text-indigo-600">Ready to begin</span>
                      </div>
                    </div>
                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-3 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-1000" style={{ width: '0%' }}></div>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">Connect backend to start configuration</p>
                  </div>
                </div>
                
                {/* Floating Cards */}
                <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-xl border border-slate-200 p-4 animate-float">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                      <Icon name="Users" size={20} className="text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Employee Base</p>
                      <p className="text-xs text-emerald-600 font-medium">Free up to 10</p>
                    </div>
                  </div>
                </div>
                
                <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-xl border border-slate-200 p-4" style={{
                  animation: 'float 3s ease-in-out infinite 1s'
                }}>
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
                      <Icon name="Zap" size={20} className="text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">AI Features</p>
                      <p className="text-xs text-purple-600 font-medium">Built-in</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-white border-y border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats?.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="flex items-center justify-center mb-2">
                    <div className="text-3xl sm:text-4xl font-bold text-slate-900">{stat?.value}</div>
                    <div className={`ml-2 flex items-center text-sm font-medium ${
                      stat?.trend === 'up' ? 'text-emerald-600' : 'text-blue-600'
                    }`}>
                      <Icon name={stat?.trend === 'up' ? 'TrendingUp' : 'Clock'} size={16} className="mr-1" />
                      {stat?.change}
                    </div>
                  </div>
                  <p className="text-slate-600 font-medium">{stat?.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* HR Capabilities Section */}
        <section className="py-24 bg-gradient-to-b from-slate-50/50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200/50 text-indigo-700 rounded-full text-sm font-medium mb-8">
                <Icon name="Sparkles" size={16} className="mr-2" />
                Complete HR Suite
              </div>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 tracking-tight">
                Everything HR teams need{' '}
                <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 bg-clip-text text-transparent">
                  in one platform
                </span>
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto font-medium leading-relaxed">
                From employee onboarding to performance management, time-off tracking to AI-powered recruiting. 
                Streamline every aspect of your people operations.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
              {hrCapabilities?.map((capability, index) => (
                <div key={index} className="group relative">
                  <div className="flex items-start space-x-6 p-8 rounded-3xl border border-slate-100 bg-white/80 backdrop-blur-sm hover:border-slate-200 hover:shadow-xl hover:shadow-slate-200/25 transition-all duration-300 hover:-translate-y-1">
                    <div className={`relative w-16 h-16 bg-gradient-to-br ${capability?.gradient} rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-${capability?.color}-500/25`}>
                      <Icon name={capability?.icon} size={28} className="text-white" />
                      <div className={`absolute inset-0 bg-gradient-to-br ${capability?.gradient} rounded-2xl blur opacity-30 group-hover:opacity-50 transition-opacity duration-300`}></div>
                    </div>
                    <div className="flex-1 pt-2">
                      <h3 className="text-xl font-semibold text-slate-900 mb-3 group-hover:text-indigo-900 transition-colors">
                        {capability?.title}
                      </h3>
                      <p className="text-slate-600 leading-relaxed font-medium">
                        {capability?.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section - Empty State */}
        {testimonials?.length > 0 ? (
          <section className="py-24 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200/50 text-indigo-700 rounded-full text-sm font-medium mb-8">
                  <Icon name="Quote" size={16} className="mr-2" />
                  Trusted by Modern Teams
                </div>
                <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6 tracking-tight">
                  What our customers say
                </h2>
              </div>
              {/* Testimonial content would go here */}
            </div>
          </section>
        ) : null}

        {/* CTA Section */}
        <section className="relative py-24 bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-700 overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(255,255,255,0.1),transparent_70%)]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(255,255,255,0.08),transparent_70%)]"></div>
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
          
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
            <div className="space-y-8">
              <div className="space-y-6">
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight">
                  Ready to streamline
                  <br />
                  <span className="bg-gradient-to-r from-white via-indigo-100 to-white bg-clip-text text-transparent">
                    your HR operations?
                  </span>
                </h2>
                <p className="text-xl text-indigo-100 max-w-2xl mx-auto font-medium leading-relaxed">
                  Join forward-thinking companies using Intime to manage employees, track performance, 
                  handle time-off, and recruit talent with AI-powered automation.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => handleStartFreeTrial('growth')}
                  size="lg" 
                  className="group bg-white text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700 px-8 py-4 text-lg font-semibold rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
                >
                  <span className="flex items-center">
                    Start free trial
                    <Icon name="ArrowRight" size={20} className="ml-2 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-2 border-white/30 text-white hover:bg-white/10 hover:border-white/50 px-8 py-4 text-lg font-semibold rounded-xl backdrop-blur-sm transition-all duration-200"
                >
                  View pricing
                  <Icon name="CreditCard" size={18} className="ml-2" />
                </Button>
              </div>
              
              <div className="flex items-center justify-center space-x-8 text-indigo-200/80">
                {[
                  { icon: 'Check', text: 'Free up to 10 employees' },
                  { icon: 'Check', text: 'No credit card required' },
                  { icon: 'Check', text: 'All features included' }
                ]?.map((item, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <Icon name={item?.icon} size={16} />
                    <span className="text-sm font-medium">{item?.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-5 gap-8">
              {/* Brand Column */}
              <div className="md:col-span-2">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center">
                    <Icon name="Zap" size={20} className="text-white" />
                  </div>
                  <span className="text-2xl font-bold tracking-tight">Intime</span>
                </div>
                <p className="text-slate-400 text-sm leading-relaxed mb-8 max-w-sm">
                  Complete HR platform combining employee management, performance tracking, time-off management, 
                  and AI-powered recruiting for modern teams.
                </p>
                <div className="flex space-x-4">
                  {['Twitter', 'Linkedin', 'Github']?.map((social, index) => (
                    <a 
                      key={index} 
                      href="#" 
                      className="w-10 h-10 bg-slate-800 hover:bg-slate-700 rounded-lg flex items-center justify-center transition-colors duration-200"
                    >
                      <Icon name={social} size={18} className="text-slate-400 hover:text-white" />
                    </a>
                  ))}
                </div>
              </div>
              
              {/* Navigation Columns */}
              {[
                {
                  title: 'Product',
                  links: [
                    { label: 'HR Features', href: '#hr-features' },
                    { label: 'AI Recruiting', href: '#recruiting' },
                    { label: 'Pricing', href: '/pricing' },
                    { label: 'Integrations', href: '#' }
                  ]
                },
                {
                  title: 'Company',
                  links: [
                    { label: 'About', href: '#' },
                    { label: 'Blog', href: '#' },
                    { label: 'Careers', href: '#' },
                    { label: 'Contact', href: 'mailto:hello@intime.com' }
                  ]
                },
                {
                  title: 'Support',
                  links: [
                    { label: 'Help Center', href: '#' },
                    { label: 'Terms', href: '/legal/terms' },
                    { label: 'Privacy', href: '/legal/privacy' },
                    { label: 'Status', href: '#' }
                  ]
                }
              ]?.map((column, index) => (
                <div key={index}>
                  <h4 className="font-semibold mb-4 text-white">{column?.title}</h4>
                  <ul className="space-y-3 text-sm">
                    {column?.links?.map((link, linkIndex) => (
                      <li key={linkIndex}>
                        {link?.href?.startsWith('/') ? (
                          <Link to={link?.href} className="text-slate-400 hover:text-white transition-colors duration-200">
                            {link?.label}
                          </Link>
                        ) : (
                          <a href={link?.href} className="text-slate-400 hover:text-white transition-colors duration-200">
                            {link?.label}
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            
            {/* Bottom Bar */}
            <div className="border-t border-slate-800 mt-16 pt-8 flex flex-col sm:flex-row justify-between items-center">
              <p className="text-sm text-slate-400">
                &copy; 2024 Intime. All rights reserved.
              </p>
              <div className="flex items-center space-x-4 mt-4 sm:mt-0">
                <div className="flex items-center space-x-2 text-sm text-slate-400">
                  <span>Built for modern HR teams</span>
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
      {/* Checkout Modal */}
      <CheckoutModal 
        isOpen={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
        selectedPlan={selectedPlan}
      />
    </>
  );
};

export default LandingPage;